import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createReminder, deleteReminder, fetchReminderById, updateReminder } from '../reminders/reminderService';
import { updateTask } from '../../store/slices/taskSlice';
import { taskDelegateIds } from '../../utils/workflowTaskUtils';

const REMINDER_KINDS = [
  { id: 'callback', label: 'Callback Reminder' },
  { id: 'check', label: 'Check Reminder' },
  { id: 'status', label: 'Status Reminder' },
];

const pad = (value) => String(value).padStart(2, '0');

const defaultDateTime = () => {
  const next = new Date(Date.now() + 60 * 60 * 1000);
  return {
    date: `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`,
    time: `${pad(next.getHours())}:${pad(next.getMinutes())}`,
  };
};

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
};

const sameUserId = (a, b) => {
  if (a == null || b == null) return false;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

const uniqueIds = (ids) => {
  const seen = new Set();
  const out = [];
  (ids || []).forEach((id) => {
    const value = String(id || '').trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
};

const mappedUserIdsForTask = (task) => {
  const delegates = taskDelegateIds(task);
  const creatorId = String(task?.createdByUserId || '').trim();
  if (delegates.length) return uniqueIds([...delegates, creatorId]);
  return uniqueIds([creatorId]);
};

const kindFromTitle = (title) => {
  const text = String(title || '').toLowerCase();
  if (text.includes('check reminder')) return 'check';
  if (text.includes('status reminder')) return 'status';
  return 'callback';
};

const normalizeClock = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return raw;
  return `${pad(match[1])}:${match[2]}`;
};

const titleForKind = (kindId, taskTitle) => {
  const kind = REMINDER_KINDS.find((item) => item.id === kindId) || REMINDER_KINDS[0];
  const name = (taskTitle || '').trim() || 'Task';
  return `${kind.label} + ${name}`;
};

const personLabel = (id, users, currentUserId) => {
  const isSelf = sameUserId(id, currentUserId);
  const match = (users || []).find((person) => sameUserId(person.id, id));
  const name = match
    ? ([match.firstName, match.lastName].filter(Boolean).join(' ').trim()
      || match.username
      || match.email
      || id)
    : (isSelf ? 'You' : id);
  return {
    id,
    isSelf,
    label: isSelf ? `You (${name === 'You' ? 'me' : name})` : name,
  };
};

const fieldLabelClass =
  'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5';
const inputClass =
  'w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';

const TaskReminderModal = ({
  isOpen,
  task,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const users = useSelector((state) => state.users?.users || []);

  const taskTitle = (task?.title || '').trim() || 'Task';
  const mappedUserIds = useMemo(() => mappedUserIdsForTask(task), [task]);
  const existingReminderId = String(task?.reminderId || '').trim();

  const [kindId, setKindId] = useState(task?.reminderKind || 'callback');
  const [title, setTitle] = useState(() => task?.reminderTitle || titleForKind('callback', taskTitle));
  const [titleTouched, setTitleTouched] = useState(Boolean(task?.reminderTitle));
  const [date, setDate] = useState(() => task?.reminderDate || defaultDateTime().date);
  const [time, setTime] = useState(() => task?.reminderTime || defaultDateTime().time);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const fallbackIds = mappedUserIdsForTask(task);
    const applyForm = ({
      kind,
      nextTitle,
      nextDate,
      nextTime,
      nextUserId,
      touchedTitle,
    }) => {
      if (cancelled) return;
      setKindId(kind);
      setTitleTouched(touchedTitle);
      setTitle(nextTitle);
      setDate(nextDate);
      setTime(nextTime);
      setSelectedUserId(nextUserId);
      setSaving(false);
      setError('');
    };

    const defaults = defaultDateTime();
    const snapshotKind = REMINDER_KINDS.some((item) => item.id === task?.reminderKind)
      ? task.reminderKind
      : kindFromTitle(task?.reminderTitle);
    const snapshotUser = String(task?.reminderUserId || '').trim();
    applyForm({
      kind: snapshotKind,
      nextTitle: task?.reminderTitle || titleForKind(snapshotKind, taskTitle),
      nextDate: task?.reminderDate || defaults.date,
      nextTime: normalizeClock(task?.reminderTime) || defaults.time,
      nextUserId: fallbackIds.find((id) => sameUserId(id, snapshotUser)) || snapshotUser || fallbackIds[0] || '',
      touchedTitle: Boolean(task?.reminderTitle),
    });

    if (!existingReminderId) {
      setLoadingReminder(false);
      return undefined;
    }

    setLoadingReminder(true);
    fetchReminderById(existingReminderId)
      .then((reminder) => {
        if (cancelled || !reminder) return;
        const rec = reminder.recurrence || {};
        const loadedTitle = reminder.title || task?.reminderTitle || '';
        const loadedKind = REMINDER_KINDS.some((item) => item.id === task?.reminderKind)
          ? task.reminderKind
          : kindFromTitle(loadedTitle);
        const loadedUser = String(
          reminder.targetUserId || reminder.userId || task?.reminderUserId || '',
        ).trim();
        applyForm({
          kind: loadedKind,
          nextTitle: loadedTitle || titleForKind(loadedKind, taskTitle),
          nextDate: rec.date || task?.reminderDate || defaults.date,
          nextTime: normalizeClock(rec.time || task?.reminderTime) || defaults.time,
          nextUserId: fallbackIds.find((id) => sameUserId(id, loadedUser)) || loadedUser || fallbackIds[0] || '',
          touchedTitle: Boolean(loadedTitle),
        });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingReminder(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, existingReminderId, task?.id, task?.reminderKind, task?.reminderTitle, task?.reminderDate, task?.reminderTime, task?.reminderUserId, taskTitle, task]);

  useEffect(() => {
    if (!isOpen || titleTouched || existingReminderId) return;
    setTitle(titleForKind(kindId, taskTitle));
  }, [isOpen, kindId, taskTitle, titleTouched, existingReminderId]);

  const mappedPeople = useMemo(() => {
    const ids = uniqueIds([...mappedUserIds, selectedUserId]);
    return ids.map((id) => personLabel(id, users, currentUser?.id));
  }, [mappedUserIds, selectedUserId, users, currentUser?.id]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleKindChange = (nextKindId) => {
    setKindId(nextKindId);
    if (!titleTouched) {
      setTitle(titleForKind(nextKindId, taskTitle));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }
    if (!date || !time) {
      setError('Date and time are required');
      return;
    }
    if (!selectedUserId) {
      setError('Select an assigned person');
      return;
    }

    const kind = REMINDER_KINDS.find((item) => item.id === kindId) || REMINDER_KINDS[0];
    const timezone = task?.reminderTimezone || browserTimezone();
    const snapshot = {
      reminderKind: kind.id,
      reminderTitle: trimmedTitle,
      reminderDate: date,
      reminderTime: time,
      reminderTimezone: timezone,
      reminderUserId: selectedUserId,
    };
    const recurrence = { type: 'once', time, date };

    setSaving(true);
    setError('');
    try {
      let reminderId = existingReminderId;
      let mode = 'created';
      const personChanged = existingReminderId
        && task?.reminderUserId
        && !sameUserId(selectedUserId, task.reminderUserId);

      if (existingReminderId && !personChanged) {
        await updateReminder(existingReminderId, {
          title: trimmedTitle,
          description: `${kind.label} for task: ${taskTitle}`,
          enabled: true,
          timezone,
          recurrence,
        });
        mode = 'updated';
      } else {
        if (existingReminderId) {
          try {
            await deleteReminder(existingReminderId);
          } catch {
            // Continue so a stale link can still be replaced.
          }
        }
        const created = await createReminder({
          title: trimmedTitle,
          description: `${kind.label} for task: ${taskTitle}`,
          enabled: true,
          timezone,
          sourceTaskId: task.id,
          recurrence,
          ...(sameUserId(selectedUserId, currentUser?.id) ? {} : { targetUserId: selectedUserId }),
        });
        reminderId = created?.id || created?._id || '';
        mode = existingReminderId ? 'moved' : 'created';
      }

      const updated = await dispatch(updateTask({
        taskId: task.id,
        taskData: {
          ...snapshot,
          reminderId,
        },
      })).unwrap();
      onSaved?.(updated || { ...task, ...snapshot, reminderId }, mode);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => { if (!saving) onClose?.(); }} />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {existingReminderId ? 'Edit reminder' : 'Add reminder'}
          </h3>
          <button
            type="button"
            onClick={() => { if (!saving) onClose?.(); }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loadingReminder ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading reminder…</p>
          ) : null}
          <div>
            <span className={fieldLabelClass}>Reminder type</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {REMINDER_KINDS.map((kind) => {
                const selected = kind.id === kindId;
                return (
                  <button
                    key={kind.id}
                    type="button"
                    onClick={() => handleKindChange(kind.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border text-left ${
                      selected
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-200'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {kind.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="task-reminder-title" className={fieldLabelClass}>Title</label>
            <input
              id="task-reminder-title"
              type="text"
              value={title}
              onChange={(event) => {
                setTitleTouched(true);
                setTitle(event.target.value);
              }}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="task-reminder-date" className={fieldLabelClass}>Date</label>
              <input
                id="task-reminder-date"
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="task-reminder-time" className={fieldLabelClass}>Time</label>
              <input
                id="task-reminder-time"
                type="time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="task-reminder-for" className={fieldLabelClass}>For</label>
            {mappedPeople.length ? (
              <select
                id="task-reminder-for"
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                className={inputClass}
              >
                <option value="" disabled>Select assigned person</option>
                {mappedPeople.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.label}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No assigned person on this task.</p>
            )}
          </div>

          {error ? (
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30">
          <button
            type="button"
            onClick={() => { if (!saving) onClose?.(); }}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !selectedUserId}
            className="px-3 py-1.5 text-[12px] font-semibold rounded-lg text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 border border-primary-600"
          >
            {saving ? 'Saving…' : existingReminderId ? 'Save reminder' : 'Create reminder'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default TaskReminderModal;
